// ================================================================
// AssistantChatsPanel — «Assistenten-Gespräche» über ALLE Firmen
//
// Mohammads Auswertungs-Sicht: wer hat was gesagt, was hat der Assistent
// geantwortet, welches Werkzeug lief — und wo lief keines. Werkzeugnamen sind
// hier ABSICHTLICH sichtbar: im Kunden-Chat sind sie interne Spur, in diesem
// Panel sind sie der eigentliche Befund.
// ================================================================
import './assistant-chats.css';
import { t } from '../../i18n/index.js';
import { icon, escapeHtml, delegate } from '../../utils/dom.js';
import { dateTime as fmtDateTime, time as fmtTime } from '../../utils/format.ts';
import { listAssistantChats, getAssistantChat } from '../../core/platformAssistant.js';

let overlay = null;
let searchTimer = null;

const s = {
  view: 'list',        // 'list' | 'detail'
  filters: { days: 30, orgId: '', userId: '', kind: '', only: '', q: '' },
  chats: [],
  stats: null,
  facets: { organizations: [], users: [] },
  truncated: false,
  loading: false,
  error: '',
  detail: null,
  detailLoading: false,
};

export function openAssistantChatsPanel() {
  if (overlay) return;
  overlay = document.createElement('div');
  overlay.className = 'ac-overlay';
  document.body.appendChild(overlay);
  document.body.style.overflow = 'hidden';
  delegate(overlay, '[data-action]', 'click', onAction);
  delegate(overlay, '[data-filter]', 'change', onFilterChange);
  delegate(overlay, '[data-filter="q"]', 'input', onSearchInput);
  s.view = 'list';
  s.detail = null;
  s.error = '';
  load();
}

function close() {
  if (!overlay) return;
  clearTimeout(searchTimer);
  overlay.remove();
  overlay = null;
  document.body.style.overflow = '';
}

async function load() {
  s.loading = true; s.error = ''; draw();
  try {
    const d = await listAssistantChats(s.filters);
    s.chats = d.chats || [];
    s.stats = d.stats || null;
    s.facets = d.facets || { organizations: [], users: [] };
    s.truncated = !!d.truncated;
  } catch (e) {
    s.error = e.message;
  } finally {
    s.loading = false; draw();
  }
}

async function openDetail(id) {
  s.view = 'detail'; s.detail = null; s.detailLoading = true; s.error = ''; draw();
  try {
    s.detail = (await getAssistantChat(id)).chat;
  } catch (e) {
    s.error = e.message;
  } finally {
    s.detailLoading = false; draw();
  }
}

// ── Ereignisse ──
function onAction(e, el) {
  switch (el.dataset.action) {
    case 'close': return close();
    case 'open': return openDetail(el.dataset.id);
    case 'back': s.view = 'list'; s.detail = null; return draw();
    case 'reload': return load();
    case 'clear-filters':
      s.filters = { days: 30, orgId: '', userId: '', kind: '', only: '', q: '' };
      return load();
    case 'chip': {
      // Schnellfilter aus der Kennzahlen-Zeile («nur Fehlschläge», «ohne Werkzeug»)
      const next = el.dataset.value;
      s.filters.only = s.filters.only === next ? '' : next;
      return load();
    }
    default: return undefined;
  }
}

function onFilterChange(e, el) {
  const key = el.dataset.filter;
  if (key === 'q') return;                       // Suche läuft über onSearchInput
  s.filters[key] = el.value;
  // Nutzer-Filter gehört zur Firma: wechselt die Firma, passt der Nutzer nicht mehr
  if (key === 'orgId') s.filters.userId = '';
  load();
}

function onSearchInput(e, el) {
  const value = el.value;
  clearTimeout(searchTimer);
  // Tippen darf nicht je Zeichen eine Abfrage auslösen — die Suche läuft
  // serverseitig über den Volltext aller Nachrichten im Fenster.
  searchTimer = setTimeout(() => {
    s.filters.q = value;
    load();
  }, 350);
}

// ── Rendern ──
function draw() {
  if (!overlay) return;
  const body = s.view === 'detail' ? detailHtml() : listHtml();
  const scroll = overlay.querySelector('.ac-body')?.scrollTop || 0;
  // Was gerade im Suchfeld STEHT, nicht was im Zustand liegt: die Abfrage läuft
  // verzögert, in der Zwischenzeit tippt der Nutzer weiter. Ohne das schluckt
  // jedes Neuzeichnen die letzten Zeichen.
  const liveSearch = overlay.querySelector('[data-filter="q"]');
  const liveQ = liveSearch ? liveSearch.value : null;
  const hadFocus = liveSearch && document.activeElement === liveSearch;
  overlay.innerHTML = `
    <div class="ac-backdrop" data-action="close"></div>
    <div class="ac-panel" role="dialog" aria-modal="true">
      <div class="ac-head">
        <div class="ac-head-title">${icon('forum', 22)}<span>${escapeHtml(t('assistantChats.title'))}</span></div>
        <button class="ac-icon-btn" data-action="close" title="${escapeHtml(t('assistantChats.close'))}">${icon('close', 20)}</button>
      </div>
      <div class="ac-body">${body}</div>
    </div>`;
  // Position der Liste über ein Neuzeichnen hinweg halten (Filter/Suche)
  const bodyEl = overlay.querySelector('.ac-body');
  if (bodyEl && s.view === 'list') bodyEl.scrollTop = scroll;
  const search = overlay.querySelector('[data-filter="q"]');
  if (search && liveQ !== null) {
    if (search.value !== liveQ) search.value = liveQ;
    if (hadFocus) { search.focus(); search.setSelectionRange(liveQ.length, liveQ.length); }
  }
}

function errBox(msg) {
  return `<div class="ac-error">${icon('error', 16)}<span>${escapeHtml(msg)}</span></div>`;
}
function muted(txt) { return `<p class="ac-muted">${escapeHtml(txt)}</p>`; }

function listHtml() {
  let h = `<p class="ac-subtitle">${escapeHtml(t('assistantChats.subtitle'))}</p>`;
  h += filtersHtml();
  if (s.error) h += errBox(s.error);
  if (s.loading) return h + muted(t('assistantChats.loading'));
  if (s.stats) h += statsHtml(s.stats);
  if (s.truncated) h += `<div class="ac-warn">${icon('info', 16)}<span>${escapeHtml(t('assistantChats.truncated'))}</span></div>`;
  if (!s.chats.length) return h + emptyHtml();
  h += `<div class="ac-list">${s.chats.map(chatRow).join('')}</div>`;
  if (s.stats && s.stats.chats > s.stats.shown) {
    h += muted(t('assistantChats.moreHint', { shown: s.stats.shown, total: s.stats.chats }));
  }
  return h;
}

function emptyHtml() {
  return `<div class="ac-empty">
      ${icon('forum', 32)}
      <p>${escapeHtml(t('assistantChats.empty'))}</p>
      <button class="ac-btn" data-action="clear-filters">${escapeHtml(t('assistantChats.clearFilters'))}</button>
    </div>`;
}

function option(value, label, selected) {
  return `<option value="${escapeHtml(value)}"${value === selected ? ' selected' : ''}>${escapeHtml(label)}</option>`;
}

function filtersHtml() {
  const f = s.filters;
  const orgs = s.facets.organizations || [];
  const users = (s.facets.users || []).filter((u) => !f.orgId || u.orgId === f.orgId);
  return `
    <div class="ac-filters">
      <div class="ac-search">
        ${icon('search', 18)}
        <input class="ac-input" data-filter="q" type="search" value="${escapeHtml(f.q)}"
               placeholder="${escapeHtml(t('assistantChats.searchPlaceholder'))}" />
      </div>
      <select class="ac-select" data-filter="days">
        ${[7, 30, 90, 365].map((d) => option(String(d), t('assistantChats.lastDays', { count: d }), String(f.days))).join('')}
      </select>
      <select class="ac-select" data-filter="orgId">
        ${option('', t('assistantChats.allCompanies'), f.orgId)}
        ${orgs.map((o) => option(o.id, `${o.name} (${o.count})`, f.orgId)).join('')}
      </select>
      <select class="ac-select" data-filter="userId">
        ${option('', t('assistantChats.allUsers'), f.userId)}
        ${users.map((u) => option(u.id, `${u.name} (${u.count})`, f.userId)).join('')}
      </select>
      <select class="ac-select" data-filter="kind">
        ${option('', t('assistantChats.allKinds'), f.kind)}
        ${option('voice', t('assistantChats.kindVoice'), f.kind)}
        ${option('text', t('assistantChats.kindText'), f.kind)}
      </select>
      <button class="ac-btn ac-btn-ghost" data-action="reload" title="${escapeHtml(t('assistantChats.reload'))}">${icon('refresh', 18)}</button>
    </div>`;
}

function statTile(value, label, extra = '') {
  return `<div class="ac-stat ${extra}">
      <div class="ac-stat-value">${escapeHtml(String(value))}</div>
      <div class="ac-stat-label">${escapeHtml(label)}</div>
    </div>`;
}

function statsHtml(st) {
  const chip = (key, label, value, tone) => `
    <button class="ac-chip ac-chip-${tone}${s.filters.only === key ? ' active' : ''}" data-action="chip" data-value="${escapeHtml(key)}">
      <span class="ac-chip-value">${escapeHtml(String(value))}</span><span>${escapeHtml(label)}</span>
    </button>`;

  const max = Math.max(1, ...st.timeline.map((d) => d.count));
  const bars = st.timeline.map((d) => `
      <span class="ac-bar" style="height:${Math.max(3, Math.round((d.count / max) * 100))}%"
            title="${escapeHtml(d.date)}: ${escapeHtml(String(d.count))}"></span>`).join('');

  const tools = st.topTools.length
    ? `<div class="ac-tools">
         <div class="ac-tools-title">${escapeHtml(t('assistantChats.topTools'))}</div>
         <div class="ac-tools-list">${st.topTools.map((tool) => `
           <span class="ac-tool${tool.failCount ? ' ac-tool-warn' : ''}" title="${escapeHtml(t('assistantChats.toolFailCount', { count: tool.failCount }))}">
             <code>${escapeHtml(tool.name)}</code><b>${escapeHtml(String(tool.count))}</b>
           </span>`).join('')}</div>
       </div>`
    : '';

  return `
    <div class="ac-stats">
      ${statTile(st.chats, t('assistantChats.statChats'))}
      ${statTile(st.users, t('assistantChats.statUsers'))}
      ${statTile(st.organizations, t('assistantChats.statCompanies'))}
      ${statTile(st.voiceChats, t('assistantChats.statVoice'))}
      ${statTile(st.toolCalls, t('assistantChats.statToolCalls'))}
      <div class="ac-timeline" title="${escapeHtml(t('assistantChats.timelineHint'))}">${bars}</div>
    </div>
    <div class="ac-chips">
      ${chip('failures', t('assistantChats.chipFailures'), st.toolFailures, 'danger')}
      ${chip('noTool', t('assistantChats.chipNoTool'), st.noToolChats, 'warn')}
      ${s.filters.only || s.filters.q || s.filters.orgId || s.filters.userId || s.filters.kind
        ? `<button class="ac-btn ac-btn-ghost" data-action="clear-filters">${escapeHtml(t('assistantChats.clearFilters'))}</button>`
        : ''}
    </div>
    ${tools}`;
}

function kindBadge(kind) {
  return kind === 'voice'
    ? `<span class="ac-badge ac-badge-voice">${icon('mic', 14)}${escapeHtml(t('assistantChats.kindVoice'))}</span>`
    : `<span class="ac-badge ac-badge-text">${icon('chat', 14)}${escapeHtml(t('assistantChats.kindText'))}</span>`;
}

function chatRow(c) {
  const who = c.user?.name || c.user?.email || '—';
  const org = c.organization?.name || '—';
  const title = c.title || c.preview || t('assistantChats.untitled');
  const marks = [];
  if (c.toolCalls) {
    marks.push(`<span class="ac-mark" title="${escapeHtml(t('assistantChats.markTools'))}">${icon('build', 14)}${escapeHtml(String(c.toolCalls))}</span>`);
  }
  if (c.toolFailures) {
    marks.push(`<span class="ac-mark ac-mark-danger" title="${escapeHtml(t('assistantChats.markFailures'))}">${icon('error', 14)}${escapeHtml(String(c.toolFailures))}</span>`);
  }
  if (c.noToolUsed) {
    marks.push(`<span class="ac-mark ac-mark-warn" title="${escapeHtml(t('assistantChats.markNoTool'))}">${icon('help', 14)}</span>`);
  }
  return `
    <button class="ac-row" data-action="open" data-id="${escapeHtml(c.id)}">
      <div class="ac-row-main">
        <div class="ac-row-title">${escapeHtml(title)}</div>
        <div class="ac-row-sub">
          <span class="ac-who">${icon('person', 14)}${escapeHtml(who)}</span>
          <span class="ac-org">${icon('corporate_fare', 14)}${escapeHtml(org)}</span>
          <span>${escapeHtml(fmtDateTime(c.updatedAt))}</span>
        </div>
      </div>
      <div class="ac-row-meta">
        ${marks.join('')}
        <span class="ac-mark" title="${escapeHtml(t('assistantChats.markMessages'))}">${icon('forum', 14)}${escapeHtml(String(c.messageCount))}</span>
        ${kindBadge(c.kind)}
      </div>
      ${icon('chevron_right', 20)}
    </button>`;
}

// ── Detail: das Gespräch im Wortlaut ──
function detailHtml() {
  let h = `<button class="ac-btn ac-btn-ghost ac-back" data-action="back">${icon('arrow_back', 18)}<span>${escapeHtml(t('assistantChats.back'))}</span></button>`;
  if (s.error) h += errBox(s.error);
  if (s.detailLoading) return h + muted(t('assistantChats.loading'));
  const c = s.detail;
  if (!c) return h + muted(t('assistantChats.notFound'));

  const who = c.user?.name || c.user?.email || '—';
  const langs = (c.summary?.languages || []).join(', ');
  h += `
    <div class="ac-detail-head">
      <div>
        <div class="ac-detail-title">${escapeHtml(c.title || t('assistantChats.untitled'))}</div>
        <div class="ac-row-sub">
          <span class="ac-who">${icon('person', 14)}${escapeHtml(who)}</span>
          <span class="ac-org">${icon('corporate_fare', 14)}${escapeHtml(c.organization?.name || '—')}</span>
          <span>${escapeHtml(fmtDateTime(c.createdAt))}</span>
          ${langs ? `<span title="${escapeHtml(t('assistantChats.languages'))}">${icon('translate', 14)}${escapeHtml(langs)}</span>` : ''}
        </div>
      </div>
      ${kindBadge(c.kind)}
    </div>
    <div class="ac-transcript">${(c.messages || []).map(messageHtml).join('')}</div>`;
  return h;
}

function messageHtml(m) {
  const stamp = m.ts ? `<span class="ac-ts">${escapeHtml(fmtTime(new Date(m.ts)))}</span>` : '';
  if (m.role === 'tool') {
    const ok = m.ok !== false;
    return `<div class="ac-msg ac-msg-tool${ok ? '' : ' ac-msg-tool-fail'}">
        ${icon(ok ? 'build' : 'error', 14)}
        <code>${escapeHtml(m.text || m.name || '')}</code>
        ${stamp}
      </div>`;
  }
  if (m.role === 'offer') {
    return `<div class="ac-msg ac-msg-note">${icon('description', 14)}<span>${escapeHtml(t('assistantChats.offerCreated'))}</span>${stamp}</div>`;
  }
  const isUser = m.role === 'user';
  const lang = m.lang ? `<span class="ac-lang">${escapeHtml(m.lang)}</span>` : '';
  return `<div class="ac-msg ${isUser ? 'ac-msg-user' : 'ac-msg-ai'}">
      <div class="ac-msg-role">${escapeHtml(isUser ? t('assistantChats.roleUser') : t('assistantChats.roleAssistant'))}${lang}${stamp}</div>
      <div class="ac-msg-text">${escapeHtml(m.text || '')}</div>
    </div>`;
}
